import { Button, DateInput, Flex, Form, Input, ModalManagerContext, TextArea } from '@pega/cosmos-react-core';
import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { PersonaFormData } from '../../../../interfaces/customer-simulator';
import * as deepDiff from 'deep-diff';

interface AddPersonaProps {
  handleFormSubmit: (data: PersonaFormData) => void;
  customer?: any;
}

const AddPersona: React.FC<AddPersonaProps> = ({ handleFormSubmit, customer }) => {
  const { customerId, bio, accountDetails } = customer || {};
  const { full_name, email_address, phone_number, address, occupation, dob } = bio || {};

  const { register, handleSubmit, errors, formState } = useForm<PersonaFormData>({
    mode: 'all',
    defaultValues: {
      fullName: full_name || '',
      emailAddress: email_address || '',
      phoneNumber: phone_number || '',
      fullAddress: address?.full_address || '',
      occupation: occupation || '',
      dob: dob || '',
      accountDetails: accountDetails || '',
      customerId: customerId || '',
    },
  });

  const updateDateFormat = () => {
    if (dob?.includes('/')) {
      const date = dob.split('/');
      return date[2] + '-' + date[0] + '-' + date[1];
    }
    return dob;
  };
  const [selectedDob, setSelectedDob] = useState(updateDateFormat);
  const translate = useLocalTranslation();
  const { ModalContext } = useContext(ModalManagerContext);
  const { dismiss } = useContext(ModalContext);

  const formatData = (data: PersonaFormData) => {
    const updatedData: any = {
      ...customer,
      customerId: data.customerId ? data.customerId : customerId,
      accountDetails: data.accountDetails,
      bio: {
        address: {
          full_address: data.fullAddress,
        },
        dob: selectedDob,
        email_address: data.emailAddress,
        full_name: data.fullName,
        phone_number: data.phoneNumber,
        occupation: data.occupation,
      },
    };

    if (customerId) updatedData['lastCustomerID'] = customerId;

    return updatedData;
  };

  const addRecord = (data: PersonaFormData) => {
    let triggerAPI: number | undefined = 1;
    const isValidData =
      data.emailAddress?.trim() &&
      data.fullAddress?.trim() &&
      data.fullName?.trim() &&
      data.occupation?.trim() &&
      data.phoneNumber?.trim() &&
      data.accountDetails?.trim();

    if (customer) {
      const prevValues: PersonaFormData = {
        fullName: full_name,
        emailAddress: email_address,
        phoneNumber: phone_number,
        fullAddress: address?.full_address,
        dob: dob,
        occupation: occupation,
        accountDetails: accountDetails,
      };
      const updatedData = { ...data, dob: selectedDob };
      triggerAPI = deepDiff.diff(prevValues, updatedData)?.filter((item) => (item as any)?.lhs || (item as any)?.rhs)
        .length;
    }
    if (formState.isValid && isValidData && selectedDob && triggerAPI) {
      const formattedData = formatData(data);
      handleFormSubmit(formattedData);
      dismiss();
    }
  };

  const handleOnSubmit = (event: any) => {
    event.preventDefault();
    handleSubmit(addRecord)();
  };

  const updateDob = (event: any) => {
    setSelectedDob(event?.valueAsISOString?.slice?.(0, 10));
  };

  const actions = (
    <>
      <Button onClick={dismiss}>{translate('cancel')}</Button>
      <Button variant="primary" disabled={!formState.isValid} onClick={handleOnSubmit}>
        {translate(customer ? 'update' : 'add')}
      </Button>
    </>
  );

  return (
    <Form actions={actions}>
      <Flex
        container={{
          colGap: 2,
          direction: 'column',
          gap: 2,
        }}
      >
        <Input
          name="customerId"
          label={translate('customer_id')}
          autoComplete="off"
          type="text"
          info={errors.customerId?.message}
          status={errors.customerId?.message ? 'error' : undefined}
          required
          ref={register({
            required: translate('customer_id_empty'),
            pattern: { value: /^[a-zA-Z0-9-]+$/, message: translate('customer_id_validate') },
          })}
        />

        <Input
          name="fullName"
          label={translate('customer_name')}
          autoComplete="off"
          type="text"
          info={errors.fullName?.message}
          status={errors.fullName?.message ? 'error' : undefined}
          required
          ref={register({ required: translate('customer_name_empty') })}
        />

        <Input
          name="emailAddress"
          label={translate('email')}
          autoComplete="off"
          type="text"
          info={errors.emailAddress?.message}
          status={errors.emailAddress?.message ? 'error' : undefined}
          required
          ref={register({ required: translate('email_empty') })}
        />

        <Input
          name="phoneNumber"
          label={translate('phone_number')}
          autoComplete="off"
          type="text"
          info={errors.phoneNumber?.message}
          status={errors.phoneNumber?.message ? 'error' : undefined}
          required
          ref={register({
            required: translate('phone_number_empty'),
            pattern: { value: /^[+]{0,1}[-\s0-9]*$/, message: translate('phone_number_validation') },
          })}
        />

        <Input
          name="fullAddress"
          label={translate('full_address')}
          autoComplete="off"
          type="text"
          info={errors.fullAddress?.message}
          status={errors.fullAddress?.message ? 'error' : undefined}
          required
          ref={register({ required: translate('full_address_empty') })}
        />

        <DateInput
          label={translate('date_of_birth')}
          value={selectedDob}
          onChange={updateDob}
          max={new Date()}
          required
        />

        <TextArea
          name="accountDetails"
          type="text"
          label={translate('accountDetails')}
          required
          resizable
          info={errors.accountDetails?.message}
          status={errors.accountDetails?.message ? 'error' : undefined}
          ref={register({ required: translate('accounts_empty') })}
        />

        <Input
          name="occupation"
          label={translate('occupation')}
          autoComplete="off"
          type="text"
          info={errors.occupation?.message}
          status={errors.occupation?.message ? 'error' : undefined}
          required
          ref={register({ required: translate('occupation_empty') })}
        />
      </Flex>
    </Form>
  );
};

export default AddPersona;
